import React, { useEffect, useState, useCallback, Suspense } from 'react';
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  ConfigProvider,
  Modal,
  notification,
  Row,
  Table,
  Tooltip,
  Dropdown,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { debouncedDataLoadWaitMs } from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import {
  getStoreCourierSource,
  deleteStoreCourierSource,
} from 'store/requests/order';

const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));

const OrderCourierManagement = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const { confirm } = Modal;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [25, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 25,
    },
  });

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      dataIndex: ['store', 'name'],
      title: t('store'),
      sorter: false,
      render: (text) => text || '--',
    },
    {
      dataIndex: ['zone', 'name'],
      title: t('zone'),
      sorter: false,
      render: (text) => text || '--',
    },
    {
      dataIndex: ['courierSource', 'name'],
      title: t('firm'),
      sorter: false,
      render: (text) => text || '--',
    },
    {
      width: 50,
      render: (dataItem) =>
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                    setSelectedItem(dataItem);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
          >
            <Button shape="circle" icon={<MoreOutlined />} />
          </Dropdown>
    },
  ];

  const showDeleteTemplate = () => {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('orderCourierManagementDeleteConfirm'),
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      async onOk() {
        await deleteStoreCourierSource(selectedRowKeys[0]);
        notification.success({
          message: t('processCompleted'),
          description: t('orderCourierManagementDeleted'),
        });
        fetchData();
      },
    });
  };

  const getQueryString = () => {
    return `page=${tableParams.pagination.current - 1}&size=${
      tableParams.pagination.pageSize
    }`;
  };

  const loadData = useCallback(
    debounce(async (queryString) => {
      if (!loading) {
        setLoading(true);
        try {
          const res = await getStoreCourierSource(queryString);
          const { content, page } = res;
          setRows(content || []);
          setSelectedRowKeys([]);
          setTableParams((prev) => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              total: page.totalElements,
            },
          }));
        } finally {
          setLoading(false);
        }
      }
    }, debouncedDataLoadWaitMs),
    [loading]
  );

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination.current, tableParams.pagination.pageSize]);

  const handleTableChange = (pagination) => {
    setTableParams((prev) => ({
      ...prev,
      pagination,
    }));
  };

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('addOrderCourierManagement'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
    },
    {
      isCrudEnabled,
      title: t('slotManagement.delete'),
      icon: <DeleteOutlined />,
      onClick: showDeleteTemplate,
      disabled: selectedRowKeys.length !== 1,
    },
  ];

  const renderActionButtons = () =>
    actions.map((item, idx) =>
      item.isCrudEnabled ? (
        <Tooltip title={item.title} key={idx}>
          <Button
            type={item.type}
            shape="round"
            icon={item.icon}
            onClick={item.onClick}
            disabled={item.disabled}
            style={{ marginBottom: '10px', marginRight: '3px' }}
          />
        </Tooltip>
      ) : null
    );

  return (
    <div>
      <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Col>{renderActionButtons()}</Col>
      </Row>

      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            rowSelection={isCrudEnabled ? rowSelection : null}
            loading={loading}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ y: '70vh' }}
          />
        </ConfigProvider>
      </Row>

      <Suspense fallback={<></>}>
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedTemplate={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
            isCrudEnabled={isCrudEnabled}
          />
        )}
      </Suspense>
    </div>
  );
};

export default OrderCourierManagement;
